import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConfigDataFrameInfo} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {JSONValue} from 'util/Types';

import {AnalysisConfigReference, AnalysisConfigSpec, AnalysisStatusReference} from './AnalysisAdapterTypes';

/**
 * Given a reference to another step, return the step object it points to.
 */
export function resolveReference(
  reference: AnalysisConfigReference | AnalysisStatusReference,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: PipelineGraphQL
): PipelineStep {
  const matchingIncomingEdge = layoutStep.incomingEdges.find(
    (incomingEdge) =>
      incomingEdge.toStepData.name === reference.inputDataName &&
      incomingEdge.toStepDataIndex === reference.inputDataIndex
  )!;
  const stepId = matchingIncomingEdge.fromStep.id;
  return analysis.steps.find(({id}) => id === stepId)!;
}

/**
 * Given a config reference, return the step and config.
 */
export function resolveConfigReference(
  configReference: AnalysisConfigReference,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: PipelineGraphQL
): {
  step: PipelineStep;
  config: JSONValue;
} {
  const step = resolveReference(configReference, layoutStep, analysis);
  const configPath = configReference.configPath;
  const config: JSONValue = configPath.reduce((currentConfig, configKey) => {
    if (Array.isArray(currentConfig)) {
      return currentConfig[configKey as number];
    } else if (typeof currentConfig === 'object' && currentConfig != null) {
      return (currentConfig as Record<string, JSONValue>)[configKey as string];
    }
    throw new Error(`Invalid path: ${configPath.join('.')}`);
  }, step.config);

  return {
    step,
    config,
  };
}

const STEP_TYPE_TO_REFERENCE_INPUT_STEP_DATA_NAMES_OVERRIDE: Partial<Record<PipelineStepType, string>> = {
  // This is a special case because the first input step data doesn't have any dataframe nodes, so any ConfigField
  // for a step of this kind fails without an override.
  [PipelineStepType.EXPERIMENT_OPTIMIZATION_RECOMMENDATION]: 'Search space dataset',
};

/**
 * Given a config reference, return the step, config, and config data frame spec for it.
 */
export function resolveConfigReferenceAndDataFrameInfo(
  configReference: AnalysisConfigReference,
  configSpec: AnalysisConfigSpec,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: PipelineGraphQL
): ReturnType<typeof resolveConfigReference> & {
  configDataFrameInfo: ConfigDataFrameInfo | null;
} {
  const {step, config} = resolveConfigReference(configReference, layoutStep, analysis);

  let inputStepDataIndex = 0;
  let inputRawDataIndex = 0;
  if (configSpec.kind === 'tableReference') {
    inputStepDataIndex = step.inputStepDatas.findIndex(
      (stepData) => stepData.name === configSpec.inputDataName
    );
    inputRawDataIndex = configSpec.inputDataIndex;
  } else if (STEP_TYPE_TO_REFERENCE_INPUT_STEP_DATA_NAMES_OVERRIDE[step.stepType]) {
    inputStepDataIndex = step.inputStepDatas.findIndex(
      (stepData) => stepData.name === STEP_TYPE_TO_REFERENCE_INPUT_STEP_DATA_NAMES_OVERRIDE[step.stepType]
    );
  }

  const dataFrame = step.inputStepDatas[inputStepDataIndex]?.rawDatas[inputRawDataIndex];

  if (dataFrame != null) {
    // We may want to remove this assertion in the future to enable configuring any kind of step
    assert(dataFrame.__typename === 'DataFrameDomainNode', 'Config steps must have an input data frame');
  }

  return {
    step,
    config,
    configDataFrameInfo: dataFrame != null ? {id: dataFrame.id, dataFrameSpec: dataFrame} : null,
  };
}
